import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { userService } from "../../../services/userService";
import { certificateService } from "../../../services/certificateService";
import LockImage from "../../../asset/images/home/lock.png";
import LogoutButton from "../../../components/LogoutButton";

const DashboardSetting = () => {
    const { user, updateUser } = useAuth();
    const [activeTab, setActiveTab] = useState(null);
    const [certificates, setCertificates] = useState([]);
    const [loadingCertificates, setLoadingCertificates] = useState(false);
    const [passwordVisibility, setPasswordVisibility] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [uploadingImage, setUploadingImage] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        if (user) {
            setProfileData({
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                phoneNumber: user.phoneNumber || "",
            });
        }
    }, [user]);

    useEffect(() => {
        if (activeTab === "certificate") {
            loadCertificates();
        }
    }, [activeTab]);

    const loadCertificates = async () => {
        setLoadingCertificates(true);
        try {
            const result = await certificateService.getCertificates();
            if (result.success) {
                setCertificates(result.data.certificates || []);
            }
        } catch (error) {
            console.error('Failed to load certificates:', error);
        } finally {
            setLoadingCertificates(false);
        }
    };

    const togglePassword = (field) => {
        setPasswordVisibility((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        
        if (error || uploadingImage) {
            return;
        }
        
        setError("");
        setSuccess("");
        setLoading(true);

        try {
            const result = await userService.updateProfile({
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                phoneNumber: profileData.phoneNumber,
            });

            if (result.success) {
                setSuccess("Profile updated successfully!");
                updateUser(result.data.user);
                setTimeout(() => {
                    setSuccess("");
                }, 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error?.message || "Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        
        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
        }

        setError("");
        setSuccess("");
        setUploadingImage(true);

        try {
            
            const reader = new FileReader();
            reader.onloadend = async () => {
                const imageUrl = reader.result; 
                
                try {
                    const result = await userService.uploadProfileImage(imageUrl);
                    if (result.success) {
                        setSuccess("Profile picture updated successfully!");
                        updateUser(result.data.user);
                        setPreviewImage(null); 
                        setTimeout(() => {
                            setSuccess("");
                        }, 3000);
                    }
                } catch (err) {
                    setError(err.response?.data?.error?.message || "Failed to upload profile picture");
                } finally {
                    setUploadingImage(false);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                }
            };
            reader.onerror = () => {
                setError("Failed to read image file");
                setUploadingImage(false);
            };
            reader.readAsDataURL(file);
            
            // Set preview immediately
            const previewReader = new FileReader();
            previewReader.onloadend = () => {
                setPreviewImage(previewReader.result);
            };
            previewReader.readAsDataURL(file);
        } catch (err) {
            setError(err.response?.data?.error?.message || "Failed to upload profile picture");
            setUploadingImage(false);
        }
    };

    const handleImageButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!passwordData.currentPassword) {
            setError("Current password is required");
            return;
        }

        if (!passwordData.newPassword) {
            setError("New password is required");
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError("New passwords do not match");
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setError("Password must be at least 8 characters long");
            return;
        }

        setLoading(true);

        try {
            const result = await userService.changePassword(
                passwordData.currentPassword,
                passwordData.newPassword
            );

            if (result.success) {
                setSuccess("Password changed successfully!");
                setPasswordData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                });
                setTimeout(() => {
                    setSuccess("");
                }, 3000);
            } else {
                setError(result.error || "Failed to change password");
            }
        } catch (err) {
            console.error('Password change error:', err);
            const errorMessage = err.response?.data?.error?.message 
                || err.response?.data?.message 
                || err.message 
                || "Failed to change password. Please check your current password and try again.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-[#E2F2FF] rounded-xl p-3 lg:p-3 shadow-md border border-blue-200">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Left Column - Settings Options */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-md p-6">
                            <h3 className="text-2xl font-bold mb-4 text-gray-600">Settings</h3>

                            {[
                                { id: "edit-profile", label: "Edit Profile", iconPath: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
                                { id: "certificate", label: "Certificate", iconPath: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
                                { id: "change-password", label: "Change Password", iconPath: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" },
                            ].map((btn) => (
                                <button
                                    key={btn.id}
                                    className={`settings-button ${activeTab === btn.id ? "active" : ""}`}
                                    onClick={() => setActiveTab(btn.id)}
                                >
                                    <div className="button-content">
                                        <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={btn.iconPath} />
                                        </svg>
                                        <span>{btn.label}</span>
                                    </div>
                                    <svg className="button-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            ))}

                            {/* Log Out Button */}
                            <div className="flex justify-center mt-20 mb-14">
                                <LogoutButton />
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Tab Content */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-md p-6">

                            {activeTab === "edit-profile" && (
                                <div className="block">
                                    <h3 className="text-2xl font-bold mb-4 text-gray-600">Edit Profile</h3>

                                    {success && (
                                        <div className="mb-6 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#f0fdf4', border: '2px solid #86efac' }}>
                                            <div className="flex items-center gap-3">
                                                <svg className="w-6 h-6 flex-shrink-0" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-sm font-semibold" style={{ color: '#15803d' }}>{success}</p>
                                            </div>
                                        </div>
                                    )}
                                    {error && (
                                        <div className="mb-6 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#fef2f2', border: '2px solid #fca5a5' }}>
                                            <div className="flex items-center gap-3">
                                                <svg className="w-6 h-6 flex-shrink-0" style={{ color: '#dc2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-sm font-semibold" style={{ color: '#991b1b' }}>{error}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex flex-col items-center">
                                        <img
                                            src={previewImage || user?.profileImage || "https://picsum.photos/seed/profile/150/150.jpg"}
                                            className="w-32 h-32 rounded-full object-cover mb-4"
                                            alt="Profile"
                                        />

                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleImageChange}
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                        />

                                        <button 
                                            onClick={handleImageButtonClick}
                                            disabled={uploadingImage}
                                            className="password-save-btn text-white py-2 px-4 rounded-full mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {uploadingImage ? "Uploading..." : "Change Profile Photo"}
                                        </button>

                                        <div className="w-full">
                                            <h2 className="text-md font-bold text-left mb-4">Provide information</h2>

                                            <form onSubmit={handleProfileUpdate} className="space-y-4 w-full">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                                    <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0
                              018 0zM12 14a7 7 0 00-7 7h14a7
                              7 0 00-7-7z"/>
                    </svg>
                </span>

                                                        <input
                                                            type="text"
                                                            placeholder="First Name"
                                                            value={profileData.firstName}
                                                            onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                                                            required
                                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md
                               focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                        />
                                                    </div>

                                                    <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0
                              018 0zM12 14a7 7 0 00-7 7h14a7
                              7 0 00-7-7z"/>
                    </svg>
                </span>

                                                        <input
                                                            type="text"
                                                            placeholder="Last Name"
                                                            value={profileData.lastName}
                                                            onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                                                            required
                                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md
                               focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                        />
                                                    </div>

                                                    <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M3 8l7.89 5.26a2 2 0
                              002.22 0L21 8M5 19h14a2
                              2 0 002-2V7a2 2 0
                              00-2-2H5a2 2 0
                              00-2 2v10a2 2 0
                              002 2z"/>
                    </svg>
                </span>

                                                        <input
                                                            type="email"
                                                            placeholder="Email"
                                                            value={profileData.email}
                                                            disabled
                                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md
                               bg-gray-100 text-sm cursor-not-allowed"
                                                        />
                                                    </div>

                                                    <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M3 5a2 2 0 012-2h3.28a1
                              1 0 01.948.684l1.498
                              4.493a1 1 0 01-.502
                              1.21l-2.257 1.13a11.042
                              11.042 0 005.516 5.516l1.13-2.257a1
                              1 0 011.21-.502l4.493
                              1.498a1 1 0 01.684.949V19a2 2 0
                              01-2 2h-1C9.716 21 3 14.284
                              3 6V5z"/>
                    </svg>
                </span>

                                                        <input
                                                            type="tel"
                                                            placeholder="Phone Number"
                                                            value={profileData.phoneNumber || ""}
                                                            onChange={(e) => setProfileData({ ...profileData, phoneNumber: e.target.value })}
                                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md
                               focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex justify-center mt-6">
                                                    <button
                                                        type="submit"
                                                        disabled={loading || uploadingImage || !!error}
                                                        className="w-full password-save-btn text-white font-medium py-4 px-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {loading ? "Saving..." : "Save Changes"}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>

                                    </div>
                                </div>
                            )}

                            {activeTab === "certificate" && (
                                <div className="block">
                                    <h3 className="text-2xl font-bold mb-4 text-gray-600">Certificate</h3>

                                    {loadingCertificates ? (
                                        <div className="text-center py-8">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                                            <p className="mt-4 text-gray-600">Loading certificates...</p>
                                        </div>
                                    ) : certificates.length > 0 ? (
                                        <div className="text-center text-gray-700">
                                            <p className="mb-2">Congratulations on achieving your certificate!</p>
                                            {certificates.map((cert) => (
                                                <div key={cert.id} className="mb-6">
                                                    <p className="text-sm text-gray-600 mb-4">Course: {cert.course?.title || 'Fast Track to Reading'}</p>
                                                    <div
                                                        className="flex justify-center p-4"
                                                        onContextMenu={(e) => e.preventDefault()}
                                                    >
                                                        <img
                                                            src="/cert_r21.jpg"
                                                            className="w-full max-w-md select-none pointer-events-none shadow-lg"
                                                            alt="Certificate preview"
                                                            draggable={false}
                                                        />
                                                    </div>
                                                    <div className="flex justify-center">
                                                        <button 
                                                            className="download-btn flex items-center gap-2"
                                                            onClick={async () => {
                                                                try {
                                                                    if (cert.id) {
                                                                        await certificateService.downloadCertificate(cert.id);
                                                                        return;
                                                                    }
                                                                    
                                                                    window.location.href = `/dashboard/certificate?courseId=${cert.courseId}`;
                                                                } catch (error) {
                                                                    console.error('Error downloading certificate:', error);
                                                                    window.location.href = `/dashboard/certificate?courseId=${cert.courseId}`;
                                                                }
                                                            }}
                                                        >
                                                            Download Certificate
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center text-gray-500 py-8">
                                            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <p className="text-lg font-medium mb-2">No Certificates Yet</p>
                                            <p className="text-sm">Complete a course to earn your certificate!</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!activeTab && (
                                <div className="block">
                                    <div className="text-center text-gray-500 py-16">
                                        <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        <p className="text-lg font-medium mb-2">Select an option to get started</p>
                                        <p className="text-sm">Choose from the menu on the left to view or edit your settings</p>
                                    </div>
                                </div>
                            )}

                            {activeTab === "change-password" && (
                                <div className="block px-4">
                                    <h3 className="text-2xl font-bold mb-4 text-gray-600">Change Password</h3>

                                    {success && (
                                        <div className="mb-6 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#f0fdf4', border: '2px solid #86efac' }}>
                                            <div className="flex items-center gap-3">
                                                <svg className="w-6 h-6 flex-shrink-0" style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-sm font-semibold" style={{ color: '#15803d' }}>{success}</p>
                                            </div>
                                        </div>
                                    )}
                                    {error && (
                                        <div className="mb-6 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#fef2f2', border: '2px solid #fca5a5' }}>
                                            <div className="flex items-center gap-3">
                                                <svg className="w-6 h-6 flex-shrink-0" style={{ color: '#dc2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-sm font-semibold" style={{ color: '#991b1b' }}>{error}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="password-container">

                                        <div className="password-icon">
                                            <div className="flex flex-col items-center">
                                                <div className="w-36 h-36 flex items-center justify-center mb-2">
                                                    <img src={LockImage} alt="lock" />
                                                </div>
                                            </div>
                                        </div>

                                        <form onSubmit={handlePasswordChange} className="w-full">

                                            <h2 className="text-md font-bold text-left mb-2">Provide information</h2>

                                            <div className="relative">
                                                <div className="password-input-group">

                                                    <span className="absolute left-3 top-4 text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                        </svg>
                    </span>

                                                    <input
                                                        type={passwordVisibility.currentPassword ? "text" : "password"}
                                                        id="currentPassword"
                                                        placeholder="Current Password"
                                                        value={passwordData.currentPassword}
                                                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                                        required
                                                        className="password-input pl-10 w-full"
                                                    />

                                                    <svg
                                                        onClick={() => togglePassword("currentPassword")}
                                                        className="password-toggle cursor-pointer"
                                                        width="20"
                                                        height="20"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0
                                 8.268 2.943 9.542 7-1.274 4.057-5.064
                                 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                                    </svg>
                                                </div>
                                            </div>

                                            <div className="border-b-2 mb-6"></div>

                                            <div className="relative">
                                                <div className="password-input-group">
                    <span className="absolute left-3 top-4 text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0
                                     00-2-2H6a2 2 0 00-2 2v6a2 2
                                     0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                        </svg>
                    </span>

                                                    <input
                                                        type={passwordVisibility.newPassword ? "text" : "password"}
                                                        id="newPassword"
                                                        placeholder="Enter New Password"
                                                        value={passwordData.newPassword}
                                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                                        required
                                                        className="password-input pl-10 w-full"
                                                    />

                                                    <svg
                                                        onClick={() => togglePassword("newPassword")}
                                                        className="password-toggle cursor-pointer"
                                                        width="20"
                                                        height="20"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                              d="M2.458 12C3.732 7.943 7.523 5
                                 12 5c4.478 0 8.268 2.943 9.542
                                 7-1.274 4.057-5.064 7-9.542
                                 7-4.477 0-8.268-2.943-9.542-7z"/>
                                                    </svg>
                                                </div>
                                            </div>

                                            <div className="relative">
                                                <div className="password-input-group">

                    <span className="absolute left-3 top-4 text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M12 15v2m-6 4h12a2 2 0
                                     002-2v-6a2 2 0
                                     00-2-2H6a2 2 0
                                     00-2 2v6a2 2
                                     0 002 2zm10-10V7a4 4 0
                                     00-8 0v4h8z"/>
                        </svg>
                    </span>

                                                    <input
                                                        type={passwordVisibility.confirmPassword ? "text" : "password"}
                                                        id="confirmPassword"
                                                        placeholder="Confirm New Password"
                                                        value={passwordData.confirmPassword}
                                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                                        required
                                                        className="password-input pl-10 w-full"
                                                    />

                                                    <svg
                                                        onClick={() => togglePassword("confirmPassword")}
                                                        className="password-toggle cursor-pointer"
                                                        width="20"
                                                        height="20"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                              d="M2.458 12C3.732 7.943 7.523 5
                                 12 5c4.478 0 8.268 2.943
                                 9.542 7-1.274 4.057-5.064
                                 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                                    </svg>
                                                </div>
                                            </div>

                                            <button 
                                                type="submit" 
                                                disabled={loading}
                                                className="password-save-btn w-full rounded-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {loading ? "Changing..." : "Save Changes"}
                                            </button>
                                        </form>
                                    </div>
                                </div>

                            )}

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DashboardSetting;
